import { FieldValues, FormProvider } from "react-hook-form"

import classNames from "classnames"

import { RJSFFormData, RJSFSchema } from "shared/types/RJSF"
import { Button, ButtonVariant } from "shared/ui/buttons/Button"
import {
  LoadingForm,
  LoadingFormProps
} from "shared/ui/rjsf-form/RJSFLoadingForm"

import { RJSFFormObjectField } from "./RJSFFormObjectField"
import { useRJSFFormViewModel } from "./hooks/useRJSFFormViewModel"
import "./styles/_rjsf.scss"
import { RJSFFormFieldProps } from "./types"

export interface RJSFFormAPI {
  reset: (defaultValues?: RJSFFormData) => void
}

export interface RJSFFormProps<T extends RJSFFormData = RJSFFormData> {
  id: string
  name?: string
  schema: RJSFSchema | null | undefined
  formData?: T | null
  isDisabled?: boolean
  isLoading?: boolean
  loadingVariant?: LoadingFormProps["variant"]
  className?: string
  submitOnChange?: boolean
  hideSubmitButton?: boolean
  submitButtonText?: string
  formAPIRef?: React.MutableRefObject<RJSFFormAPI>
  onChange?: (formData: T, path?: string) => void
  onSubmit?: (formData: T, path?: string) => void

  FieldInformationRenderer?: React.ComponentType<RJSFFormFieldProps>
}

export function RJSFForm<T extends FieldValues>(props: RJSFFormProps<T>) {
  const {
    id,
    methods,
    schema,
    isLoading,
    isDisabled,
    className,
    loadingVariant = "box",
    hideSubmitButton,
    submitButtonText,
    onSubmit,
    FieldInformationRenderer
  } = useRJSFFormViewModel(props)

  if (isLoading) {
    return <LoadingForm className={className} variant={loadingVariant} />
  }

  if (!schema) {
    return null
  }

  return (
    <FormProvider {...methods}>
      <form
        id={id}
        onSubmit={onSubmit}
        className={classNames("rjsf-form", className)}
      >
        <RJSFFormObjectField
          schema={schema}
          isDisabled={isDisabled}
          FieldInformationRenderer={FieldInformationRenderer}
        />
        {hideSubmitButton ? null : (
          <div className="rjsf-form__controls">
            <Button
              variant={ButtonVariant.PRIMARY}
              onClick={onSubmit}
              type="submit"
              disabled={isDisabled}
            >
              {submitButtonText}
            </Button>
          </div>
        )}
      </form>
    </FormProvider>
  )
}
